/**
 * Global toast bus - call `toast()` from anywhere (React, axios, QueryClient).
 * ToastProvider registers the renderer on mount.
 */

export type ToastVariant = "default" | "destructive" | "success";

export interface ToastInput {
  title?: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
  /**
   * Generic safety-net toast (axios interceptor, global mutation handler).
   * Dropped when the screen shows its own error toast for the same failure.
   */
  fallback?: boolean;
}

type ToastHandler = (data: ToastInput) => string;

let handler: ToastHandler | null = null;

/** Called by ToastProvider - do not use directly. */
export function registerToastHandler(fn: ToastHandler | null): void {
  handler = fn;
}

/** Show a toast notification. Safe to call before provider mounts (no-op). */
export function toast(data: ToastInput): string {
  if (!handler) {
    if (import.meta.env.DEV) {
      console.debug("[toast]", data.title, data.description);
    }
    return "";
  }
  return handler(data);
}

export function toastSuccess(title: string, description?: string): string {
  return toast({ variant: "success", title, description });
}

export function toastError(title: string, description?: string): string {
  return toast({ variant: "destructive", title, description });
}

/** Read and clear a one-shot auth notice (e.g. after logout redirect). */
const AUTH_NOTICE_KEY = "ehr_auth_notice";

export function setAuthNotice(data: ToastInput): void {
  sessionStorage.setItem(AUTH_NOTICE_KEY, JSON.stringify(data));
}

export function consumeAuthNotice(): ToastInput | null {
  const raw = sessionStorage.getItem(AUTH_NOTICE_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(AUTH_NOTICE_KEY);
  try {
    return JSON.parse(raw) as ToastInput;
  } catch {
    return null;
  }
}
